import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import {
  Books, BookOpen, Trash, ArrowsClockwise, CloudArrowDown, FileArrowUp,
  CheckCircle, WarningCircle, FileText, Database,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function KnowledgePage() {
  const [sources, setSources] = useState([]);
  const [stats, setStats] = useState({ sources: 0, chunks: 0, chat_sessions: 0 });
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [ingestUrl, setIngestUrl] = useState('');
  const [ingestLabel, setIngestLabel] = useState('');
  const [ingesting, setIngesting] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [s, st] = await Promise.all([
        fetch(`${API}/api/knowledge/sources`, { credentials: 'include' }).then(r => r.json()),
        fetch(`${API}/api/knowledge/stats`, { credentials: 'include' }).then(r => r.json()),
      ]);
      setSources(s.sources || []);
      setStats(st);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const seedArera = async () => {
    if (!window.confirm('Scaricare e indicizzare i Testi Integrati ARERA mancanti?\n' +
                        'I documenti già indicizzati verranno saltati (idempotente).\n' +
                        'Tempo stimato: 2-4 minuti per il primo run completo.\n' +
                        'Costo: zero (embeddings locali).')) return;
    setSeeding(true);
    setSeedResult(null);
    try {
      const res = await fetch(`${API}/api/knowledge/seed-arera`, {
        method: 'POST', credentials: 'include',
      });
      const d = await res.json();
      setSeedResult(d);
      const newOnes = (d.total_ok || 0) - (d.total_skipped || 0);
      const skipped = d.total_skipped || 0;
      const failed = d.total_failed || 0;
      const parts = [];
      if (newOnes > 0) parts.push(`${newOnes} nuovi`);
      if (skipped > 0) parts.push(`${skipped} già presenti`);
      if (failed > 0) parts.push(`${failed} falliti`);
      toast.success(`Seed completato: ${parts.join(' · ')}`);
      fetchAll();
    } catch (e) {
      toast.error('Seed fallito');
    } finally {
      setSeeding(false);
    }
  };

  const deleteSource = async (sid) => {
    if (!window.confirm('Eliminare definitivamente questa fonte e tutti i suoi chunk?')) return;
    const res = await fetch(`${API}/api/knowledge/sources/${sid}`, {
      method: 'DELETE', credentials: 'include',
    });
    if (res.ok) {
      toast.success('Fonte eliminata');
      fetchAll();
    } else toast.error('Errore eliminazione');
  };

  const submitIngestUrl = async () => {
    if (!ingestUrl || !ingestLabel) { toast.error('URL e etichetta richiesti'); return; }
    setIngesting(true);
    try {
      const res = await fetch(`${API}/api/knowledge/ingest-url`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: ingestUrl, label: ingestLabel }),
      });
      const d = await res.json();
      if (res.ok) {
        toast.success(`Indicizzato: ${d.chunks} chunk da ${d.pages} pagine`);
        setIngestUrl(''); setIngestLabel('');
        fetchAll();
      } else toast.error(d.detail || 'Errore');
    } finally { setIngesting(false); }
  };

  if (loading) return <div className="p-8 text-slate-500">Caricamento knowledge base...</div>;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="knowledge-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Books size={32} weight="duotone" className="text-orange-500" />
            Knowledge Base ARERA
          </h1>
          <p className="text-slate-500 mt-1">
            L'AI legge i Testi Integrati ARERA e cita gli articoli esatti. Embeddings locali (CPU, gratis), retrieval ibrido BM25 + semantica.
          </p>
        </div>
      </div>

      {/* STATS ROW */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <KPI icon={FileText} label="Fonti indicizzate" value={stats.sources} color="bg-orange-50 text-orange-600" />
        <KPI icon={Database} label="Chunk indicizzati" value={stats.chunks.toLocaleString('it-IT')} color="bg-purple-50 text-purple-600" />
        <KPI icon={BookOpen} label="Conversazioni Esperto" value={stats.chat_sessions} color="bg-blue-50 text-blue-600" />
      </div>

      {/* ACTIONS */}
      <div className="bg-white border rounded-lg p-4 mb-4">
        <h2 className="font-semibold text-slate-900 mb-3">Inizializzazione rapida</h2>
        <div className="flex flex-wrap gap-3">
          <Button onClick={seedArera} disabled={seeding}
                  className="bg-orange-500 hover:bg-orange-600" data-testid="seed-arera-btn">
            <CloudArrowDown size={16} className="mr-2" />
            {seeding ? 'Indicizzazione in corso (2-4 min)...' : 'Carica Testi Integrati ARERA'}
          </Button>
          <Button variant="outline" onClick={fetchAll}>
            <ArrowsClockwise size={16} className="mr-2" /> Ricarica
          </Button>

          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline">
                <FileArrowUp size={16} className="mr-2" /> Aggiungi PDF da URL
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Aggiungi un nuovo documento</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-slate-500">URL PDF</label>
                  <Input value={ingestUrl} onChange={(e) => setIngestUrl(e.target.value)}
                         placeholder="https://..." data-testid="ingest-url-input" />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Etichetta</label>
                  <Input value={ingestLabel} onChange={(e) => setIngestLabel(e.target.value)}
                         placeholder="es. Delibera 320/2025/R/EEL"
                         data-testid="ingest-label-input" />
                </div>
                <Button onClick={submitIngestUrl} disabled={ingesting}
                        className="bg-orange-500 hover:bg-orange-600 w-full" data-testid="ingest-submit-btn">
                  {ingesting ? 'Indicizzazione...' : 'Scarica e indicizza'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {seedResult && (
          <div className="mt-3 border rounded p-3 bg-slate-50">
            <div className="text-sm font-medium mb-2">
              Risultato seed: {seedResult.total_ok} OK
              {seedResult.total_skipped > 0 && ` (${seedResult.total_skipped} già presenti)`}
              {seedResult.total_failed > 0 && ` · ${seedResult.total_failed} falliti`}
            </div>
            <div className="space-y-1 text-xs">
              {(seedResult.seeded || []).map((s, i) => (
                <div key={i} className="flex items-center gap-2">
                  {s.ok ? <CheckCircle size={14} className={s.skipped ? "text-slate-400" : "text-green-600"} />
                        : <WarningCircle size={14} className="text-red-600" />}
                  <span className="font-medium">{s.label}</span>
                  {s.skipped && <span className="text-slate-500">({s.reason})</span>}
                  {s.ok && !s.skipped && <span className="text-slate-500">({s.chunks} chunk, {s.pages} pag.)</span>}
                  {!s.ok && <span className="text-red-500">{s.error}</span>}
                </div>
              ))}
            </div>
            {seedResult.total_failed > 0 && (
              <Button size="sm" variant="outline" onClick={seedArera}
                      className="mt-2 border-orange-300 text-orange-600">
                <ArrowsClockwise size={14} className="mr-1" /> Riprova solo i falliti
              </Button>
            )}
          </div>
        )}
      </div>

      {/* SOURCES LIST */}
      <div className="bg-white border rounded-lg">
        <div className="border-b p-3 font-semibold text-slate-900">
          Fonti indicizzate ({sources.length})
        </div>
        {sources.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <Books size={56} weight="duotone" className="mx-auto text-slate-300 mb-3" />
            <p>Nessuna fonte. Premi "Carica Testi Integrati ARERA" per iniziare.</p>
          </div>
        ) : (
          <div className="divide-y">
            {sources.map(s => (
              <div key={s.source_id} className="p-3 flex items-center justify-between hover:bg-slate-50">
                <div className="flex items-start gap-3 flex-1">
                  <FileText size={20} weight="duotone" className="text-orange-500 mt-0.5" />
                  <div>
                    <div className="font-medium text-sm">{s.label}</div>
                    <div className="text-xs text-slate-500 mt-0.5 flex gap-3">
                      <Badge variant="outline" className="text-[10px]">{s.kind}</Badge>
                      <span>{s.chunk_count} chunk</span>
                      <span>{(s.size_bytes ? Math.round(s.size_bytes / 1024) : 0)} KB</span>
                      <span>Indicizzato: {(s.ingested_at || '').slice(0, 10)}</span>
                    </div>
                  </div>
                </div>
                <Button size="sm" variant="ghost"
                        onClick={() => deleteSource(s.source_id)}
                        data-testid={`delete-source-${s.source_id}`}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50">
                  <Trash size={16} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4 text-xs text-slate-400 italic">
        💡 Tip: l'icona libro (📚) in alto a destra apre la chat "Esperto ARERA" da qualunque pagina.
        I commerciali possono chiedere spiegazioni sulle voci di fattura direttamente dalla pagina Fatture OCR.
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white border rounded-lg p-4">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded flex items-center justify-center ${color}`}>
          <Icon size={20} weight="duotone" />
        </div>
        <div>
          <div className="text-xs text-slate-500">{label}</div>
          <div className="text-2xl font-bold text-slate-900">{value}</div>
        </div>
      </div>
    </div>
  );
}
